import React, { useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import CommandCenterHome from './components/CommandCentre/CommandCenterHome/CommandCenterHome';
import CommandCenterShell from './components/CommandCentre/CommandCenterShell/CommandCenterShell';
import { ChatProvider } from './helpers/SocketContext';
import { adaptAuthenticatedUserToCommandCentreAuth } from './helpers/AuthContext';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import ProtectedRoute from './auth/ProtectedRoute';
import LoginPage from './pages/LoginPage/LoginPage';
import LegacyRouteRedirect from './pages/LoginPage/LegacyRouteRedirect';
import { buildLoginPath } from './helpers/AuthRedirect';
import MvpTalentModule from './modules/mvpTalent/MvpTalentModule';
import SystemOperationsPage from './modules/system/SystemOperationsPage';
import EzeeAiAdminPage from './modules/ezeeAi/EzeeAiAdminPage';
import AuthStatusCard from './auth/AuthStatusCard';
import AccountsPage from './modules/growth/AccountsPage';
import RevenuePage from './modules/revenue/RevenuePage';
import './App.css';

const AppRoutes: React.FC = () => {
  const { status, user } = useAuth();
  const location = useLocation();

  const commandCentreAuth = useMemo(
    () => (user ? adaptAuthenticatedUserToCommandCentreAuth(user) : null),
    [user]
  );

  const defaultAuthenticatedPath = '/';

  return (
    <div className="App">
      <Helmet>
        <title>
          {status === 'authenticated' && commandCentreAuth?.name
            ? `${commandCentreAuth.name} Command Centre`
            : 'Command Centre'}
        </title>
      </Helmet>

      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={
          <ProtectedRoute>
            <CommandCenterShell />
          </ProtectedRoute>
        }>
          <Route path="/" element={<CommandCenterHome commandCentreAuth={commandCentreAuth} appLoading={status === 'initializing'} />} />
          <Route path="/command-center/mvp-talent/*" element={<MvpTalentModule />} />
          <Route path="/system" element={<SystemOperationsPage />} />
          <Route path="/growth/accounts" element={<AccountsPage />} />
          <Route path="/revenue" element={<RevenuePage />} />
          <Route path="/ezee-ai" element={user?.commandCentreAccess?.enabled === true && user?.platformRole === "elite-core-platform-administrator" ? <EzeeAiAdminPage /> : <AuthStatusCard title="Ezee Administrator Access Required" message="Only authorized Elite Core platform administrators can manage Ezee AI settings." />} />
        </Route>

        <Route path="/dashboard/command-centre" element={<LegacyRouteRedirect basePath={defaultAuthenticatedPath} />} />
        <Route path="/dashboard/command-centre/:login_unique_id" element={<LegacyRouteRedirect basePath={defaultAuthenticatedPath} />} />
        <Route path="/dashboard/command-center" element={<LegacyRouteRedirect basePath={defaultAuthenticatedPath} />} />
        <Route path="/dashboard/command-center/:login_unique_id" element={<LegacyRouteRedirect basePath={defaultAuthenticatedPath} />} />
        <Route path="/dashboard/login" element={<LegacyRouteRedirect basePath="/login" />} />
        <Route path="/dashboard/login/:login_unique_id" element={<LegacyRouteRedirect basePath="/login" />} />

        <Route path="*" element={<Navigate to={status === 'authenticated' ? defaultAuthenticatedPath : buildLoginPath(undefined, location.search)} replace />} />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ChatProvider>
          <AppRoutes />
        </ChatProvider>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
